import React, { useEffect, useRef, useState } from 'react';
import { Brain, Cpu, TrendingUp, Search, BarChart3, Zap } from 'lucide-react';

const AIAnalyzeAnimation = () => {
  const canvasRef = useRef(null);
  const [strategiesScanned, setStrategiesScanned] = useState(0);
  const [currentStrategy, setCurrentStrategy] = useState('Algo FX Master');

  const strategies = [
    'Algo FX Master', 'Area101 Pro', 'Trading Assassin 007', 'KING OF USD', 
    'Titan AI Trades', 'Neural Network FX', 'Quantum Trader AI', 'Smart Scalper Pro',
    'AI Momentum Master', 'Deep Learning Forex', 'Alpha Generator AI', 'Trend Hunter Pro',
    'Market Genius AI', 'Profit Maximizer', 'Wave Rider Strategy', 'Golden Cross AI'
  ];

  // Counter animation
  useEffect(() => {
    const interval = setInterval(() => {
      setStrategiesScanned(prev => {
        const next = prev + Math.floor(Math.random() * 50) + 20;
        return next > 9999 ? Math.floor(Math.random() * 3000) + 1000 : next;
      });
    }, 150);

    return () => clearInterval(interval);
  }, []);

  // Strategy name rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStrategy(strategies[Math.floor(Math.random() * strategies.length)]);
    }, 800);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Generate particles
    const particles = Array.from({ length: 50 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: Math.random() * 2 + 1
    }));

    let animationId;
    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        // Draw connections - RED
        particles.slice(i + 1).forEach(p2 => {
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 100) {
            ctx.strokeStyle = `rgba(227, 24, 55, ${0.3 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });

        // Draw particle - BRIGHT RED
        ctx.fillStyle = 'rgba(255, 23, 68, 0.9)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
        
        // Add red glow
        ctx.shadowColor = 'rgba(227, 24, 55, 0.5)';
        ctx.shadowBlur = 10;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationId);
  }, []);

  return (
    <div className="ai-animation-wrapper" style={{ 
      position: 'relative',
      width: '100%',
      minHeight: '600px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)'
    }}>
      <canvas ref={canvasRef} className="ai-canvas" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.7
      }} />
      
      <div className="ai-content-overlay" style={{
        position: 'relative',
        zIndex: 2,
        maxWidth: '1200px',
        width: '100%',
        textAlign: 'center'
      }}>
        {/* Title Section */}
        <div className="ai-title-section" style={{ marginBottom: '40px' }}>
          <h2 className="ai-main-title" style={{ 
            color: '#FFFFFF',
            textShadow: '0 0 20px rgba(227, 24, 55, 0.5)'
          }}>AI Scanning Ribuan Strategi</h2>
          <p className="ai-subtitle" style={{ 
            color: 'rgba(255, 255, 255, 0.8)' 
          }}>Analisis real-time dari pola trading global</p>
        </div>

        {/* Scanning Status */}
        <div className="ai-scanning-status" style={{
          background: 'linear-gradient(135deg, rgba(227, 24, 55, 0.15), rgba(255, 23, 68, 0.1))',
          border: '2px solid rgba(227, 24, 55, 0.5)',
          borderRadius: '20px',
          padding: window.innerWidth <= 768 ? '24px 16px' : '32px',
          marginBottom: '48px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 30px rgba(227, 24, 55, 0.3), inset 0 0 30px rgba(227, 24, 55, 0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: window.innerWidth <= 768 ? '12px' : '16px', 
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <Search className="scanning-icon" style={{
              width: window.innerWidth <= 768 ? '28px' : '32px',
              height: window.innerWidth <= 768 ? '28px' : '32px',
              color: '#FF1744',
              filter: 'drop-shadow(0 0 10px rgba(255, 23, 68, 0.5))'
            }} />
            <div style={{ 
              fontSize: window.innerWidth <= 768 ? '1rem' : '1.125rem', 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontWeight: 600 
            }}>
              Sedang Scanning:
            </div>
            <div className="current-strategy" style={{
              fontSize: window.innerWidth <= 768 ? '1.125rem' : '1.25rem',
              color: '#FF1744',
              fontWeight: 700,
              minWidth: window.innerWidth <= 768 ? 'auto' : '200px',
              textAlign: 'center',
              textShadow: '0 0 15px rgba(255, 23, 68, 0.5)'
            }}>
              {currentStrategy}
            </div>
          </div>

          <div style={{ 
            fontSize: window.innerWidth <= 768 ? '2.5rem' : '3rem', 
            fontWeight: 800,
            background: 'linear-gradient(135deg, #E31837 0%, #FF1744 50%, #FF6B7A 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '12px',
            fontFamily: "'Space Grotesk', monospace",
            filter: 'drop-shadow(0 0 20px rgba(227, 24, 55, 0.5))'
          }}>
            {strategiesScanned.toLocaleString()}+
          </div>
          <div style={{ 
            fontSize: window.innerWidth <= 768 ? '0.9375rem' : '1rem', 
            color: 'rgba(255, 255, 255, 0.7)', 
            marginBottom: '20px' 
          }}>
            Strategi Dianalisis Hari Ini
          </div>

          {/* Progress Bars - RED THEME */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <BarChart3 style={{ 
                width: '20px', 
                height: '20px', 
                color: '#FF1744',
                filter: 'drop-shadow(0 0 8px rgba(255, 23, 68, 0.5))'
              }} />
              <div style={{ 
                flex: 1, 
                height: '10px', 
                background: 'rgba(227, 24, 55, 0.2)', 
                borderRadius: '8px', 
                overflow: 'hidden',
                border: '1px solid rgba(227, 24, 55, 0.3)'
              }}>
                <div className="scan-progress-1" style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #E31837, #FF1744)',
                  borderRadius: '8px',
                  width: '75%',
                  boxShadow: '0 0 15px rgba(227, 24, 55, 0.6)'
                }}></div>
              </div>
              <span style={{ 
                fontSize: '0.875rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                minWidth: '45px',
                fontWeight: 600
              }}>75%</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Zap style={{ 
                width: '20px', 
                height: '20px', 
                color: '#FF6B7A',
                filter: 'drop-shadow(0 0 8px rgba(255, 107, 122, 0.5))'
              }} />
              <div style={{ 
                flex: 1, 
                height: '10px', 
                background: 'rgba(227, 24, 55, 0.2)', 
                borderRadius: '8px', 
                overflow: 'hidden',
                border: '1px solid rgba(227, 24, 55, 0.3)'
              }}>
                <div className="scan-progress-2" style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #C4122F, #FF1744)',
                  borderRadius: '8px',
                  width: '92%',
                  boxShadow: '0 0 15px rgba(227, 24, 55, 0.6)'
                }}></div>
              </div>
              <span style={{ 
                fontSize: '0.875rem', 
                color: 'rgba(255, 255, 255, 0.9)', 
                minWidth: '45px',
                fontWeight: 600
              }}>92%</span>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          width: '100%'
        }}>
          <div className="ai-feature-box" style={{
            background: 'rgba(227, 24, 55, 0.08)',
            border: '1px solid rgba(227, 24, 55, 0.3)'
          }}>
            <Brain className="feature-icon" style={{
              width: '56px',
              height: '56px',
              color: '#FF1744',
              margin: '0 auto 20px',
              display: 'block',
              filter: 'drop-shadow(0 0 15px rgba(255, 23, 68, 0.5))'
            }} />
            <h3 style={{ color: '#FFFFFF' }}>10,000+ Strategi</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Dipantau terus-menerus</p>
          </div>

          <div className="ai-feature-box" style={{
            background: 'rgba(227, 24, 55, 0.08)',
            border: '1px solid rgba(227, 24, 55, 0.3)'
          }}>
            <Cpu className="feature-icon" style={{
              width: '56px',
              height: '56px',
              color: '#FF1744',
              margin: '0 auto 20px',
              display: 'block',
              filter: 'drop-shadow(0 0 15px rgba(255, 23, 68, 0.5))'
            }} />
            <h3 style={{ color: '#FFFFFF' }}>Scoring Real-Time</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Ranking powered by AI</p>
          </div>

          <div className="ai-feature-box" style={{
            background: 'rgba(227, 24, 55, 0.08)',
            border: '1px solid rgba(227, 24, 55, 0.3)'
          }}>
            <TrendingUp className="feature-icon" style={{
              width: '56px',
              height: '56px',
              color: '#FF1744',
              margin: '0 auto 20px',
              display: 'block',
              filter: 'drop-shadow(0 0 15px rgba(255, 23, 68, 0.5))'
            }} />
            <h3 style={{ color: '#FFFFFF' }}>Performa Terbaik</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Dipilih secara otomatis</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAnalyzeAnimation;
